import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageHero from "@/components/PageHero";
import { Cable, PhoneCall, Tv, Server, Building, CheckCircle2, ArrowRight } from "lucide-react";

const systems = [
  {
    icon: Cable,
    title: "Structured Cabling & Fiber Optics",
    desc: "Certified Cat6/Cat6A copper and fiber optic backbones linking every floor, room and device — Fluke-tested and documented.",
    points: ["Cat6 / Cat6A horizontal cabling", "Single-mode & multi-mode fiber backbone", "Patch panels, containment & labelling"],
    chips: ["CAT6A", "OS2 FIBER", "FLUKE TESTED"],
    img: "https://images.unsplash.com/photo-1691435828932-911a7801adfb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxzbWFydCUyMGJ1aWxkaW5nJTIwZWx2JTIwc3lzdGVtJTIwc2VydmVyJTIwcm9vbSUyMG5ldHdvcmslMjBjYWJsaW5nfGVufDB8fHx8MTc4ODM0MTM0MHww&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: PhoneCall,
    title: "Intercom Systems",
    desc: "Audio and video intercom for condos, offices and gated facilities — verify visitors at the door, gate or guardhouse before granting access.",
    points: ["IP video intercom with door release", "Multi-unit apartment systems", "Guardhouse & lobby station integration"],
    chips: ["IP VIDEO", "DOOR RELEASE", "MULTI-TENANT"],
    img: "https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxzbWFydCUyMGJ1aWxkaW5nJTIwZWx2JTIwc3lzdGVtJTIwc2VydmVyJTIwcm9vbSUyMG5ldHdvcmslMjBjYWJsaW5nfGVufDB8fHx8MTc4ODM0MTM0MHww&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: Tv,
    title: "SMATV / IPTV",
    desc: "Master antenna TV and IPTV distribution delivering satellite and digital channels across hotels, residences and commercial buildings.",
    points: ["Centralised satellite & digital headend", "Distribution to hundreds of points", "IPTV with hotel / corporate portals"],
    chips: ["HEADEND", "IPTV", "MULTI-POINT"],
    img: "https://images.unsplash.com/photo-1785682231847-93265d8e633d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHszfHxzbWFydCUyMGJ1aWxkaW5nJTIwZWx2JTIwc3lzdGVtJTIwc2VydmVyJTIwcm9vbSUyMG5ldHdvcmslMjBjYWJsaW5nfGVufDB8fHx8MTc4ODM0MTM0MHww&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: Server,
    title: "Network & Server Room",
    desc: "Racks, containment, cable management, UPS and cooling — a clean, maintainable core for your IT and security infrastructure.",
    points: ["Server racks & structured patching", "UPS & power distribution", "Environmental monitoring & cooling"],
    chips: ["42U RACK", "UPS", "MONITORING"],
    img: "https://images.unsplash.com/photo-1691435828932-911a7801adfb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxzbWFydCUyMGJ1aWxkaW5nJTIwZWx2JTIwc3lzdGVtJTIwc2VydmVyJTIwcm9vbSUyMG5ldHdvcmslMjBjYWJsaW5nfGVufDB8fHx8MTc4ODM0MTM0MHww&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: Building,
    title: "Smart Building Integration",
    desc: "Bring ELV subsystems together — CCTV, access, intercom and PA talking to each other on one managed network.",
    points: ["Unified ELV network design", "Cross-system event linkage", "Central monitoring dashboards"],
    chips: ["INTEGRATED", "SCALABLE", "MANAGED"],
    img: "https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxzbWFydCUyMGJ1aWxkaW5nJTIwZWx2JTIwc3lzdGVtJTIwc2VydmVyJTIwcm9vbSUyMG5ldHdvcmslMjBjYWJsaW5nfGVufDB8fHx8MTc4ODM0MTM0MHww&ixlib=rb-4.1.0&q=85",
  },
];

export default function ElvSystems() {
  return (
    <div data-testid="elv-systems-page">
      <PageHero
        label="ELV Systems"
        title="Extra Low Voltage Infrastructure, Engineered Right"
        subtitle="Structured cabling, intercom, SMATV/IPTV and network infrastructure — the invisible backbone every smart building depends on."
        image="https://images.unsplash.com/photo-1691435828932-911a7801adfb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxzbWFydCUyMGJ1aWxkaW5nJTIwZWx2JTIwc3lzdGVtJTIwc2VydmVyJTIwcm9vbSUyMG5ldHdvcmslMjBjYWJsaW5nfGVufDB8fHx8MTc4ODM0MTM0MHww&ixlib=rb-4.1.0&q=85"
      />

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {systems.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
                className="glow-card overflow-hidden flex flex-col"
                data-testid={`elv-card-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="relative h-44 overflow-hidden">
                  <img src={s.img} alt={s.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] to-transparent" />
                  <div className="absolute bottom-4 left-4 w-10 h-10 bg-blue-600 flex items-center justify-center border border-cyan-400/40">
                    <s.icon className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-display text-lg font-semibold text-white">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                  <ul className="mt-4 space-y-2">
                    {s.points.map((p) => (
                      <li key={p} className="flex gap-2 text-sm text-slate-300">
                        <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" /> {p}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 flex flex-wrap gap-2 pt-2">
                    {s.chips.map((c) => <span key={c} className="spec-chip">{c}</span>)}
                  </div>
                </div>
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="glow-card p-8 flex flex-col justify-center items-start bg-blue-600/10"
              data-testid="elv-cta-card"
            >
              <span className="tech-label">New Build or Retrofit?</span>
              <h3 className="font-display text-xl font-semibold text-white mt-2">
                Get an ELV design &amp; quotation
              </h3>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                Share your floor plans or site details — we will propose a compliant,
                future-proof ELV layout with itemised pricing.
              </p>
              <Link
                to="/quote"
                data-testid="elv-quote-cta"
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition-colors border border-cyan-400/40"
              >
                Request a Quote <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
